import React, { useEffect, useState } from "react";
import { Badge } from "react-bootstrap";
import {
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Switch,
  Divider,
  CircularProgress,
  TextField,
  Button,
} from "@mui/material";
import { MdLocationOn, MdLocalShipping, MdFileDownload } from "react-icons/md";
import axios from "axios";
import moment from "moment";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

/**
 * Detrack tracking details popup - shared between the Order Master list
 * (manage.js, opened from the "Detrack Status" badge) and the Order detail
 * page (opened from the icon bar). Fetches live from the Detrack API (not
 * local DB) whenever it opens, so POD image URLs (S3 signed, ~15min expiry)
 * are always fresh. Shows: tracking milestones table, POD details, POD
 * images (hover to zoom), and Shipping Label / POD PDF downloads.
 */
function DetrackStatusDialog({ open, onClose, orderUniqueId }) {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [exportLoading, setExportLoading] = useState(""); // "label-{idx}" or "pod-{idx}" during PDF download

  useEffect(() => {
    if (!open || !orderUniqueId) return;
    let cancelled = false;

    const fetchDetrackDetails = async () => {
      setLoading(true);
      setData([]);
      try {
        const res = await axios.get(
          API_BASE_URL + `api/detrack/tracking/${orderUniqueId}`,
          { headers: { "x-access-token": localStorage.getItem("token") } }
        );
        if (cancelled) return;
        // Backend returns an array of delivery records
        const resData = res.data.data;
        setData(Array.isArray(resData) ? resData : [resData]);
      } catch (err) {
        if (!cancelled) console.error("Failed to fetch Detrack details:", err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchDetrackDetails();
    return () => {
      cancelled = true;
    };
  }, [open, orderUniqueId]);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md" className="GeneralModal">
      <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        Detrack Tracking Details
        {orderUniqueId && (
          <span style={{ fontSize: "16px", fontWeight: "normal", color: "#ffffff" }}>
            Order No : {orderUniqueId}
          </span>
        )}
        <IconButton onClick={onClose} size="small">
          ✕
        </IconButton>
      </DialogTitle>
      <DialogContent>
        {loading ? (
          <div style={{ display: "flex", justifyContent: "center", padding: "40px" }}>
            <CircularProgress />
          </div>
        ) : data.length === 0 ? (
          <div style={{ textAlign: "center", padding: "40px", color: "#999" }}>
            No tracking data available.
          </div>
        ) : (
          <>
            {/* Summary banner — only shown when multiple delivery records exist */}
            {data.length > 1 && (() => {
              const statusCounts = {};
              data.forEach((r) => {
                const s = (r.tracking_status || r.status || "unknown").replace(/_/g, " ");
                const label = s.charAt(0).toUpperCase() + s.slice(1);
                statusCounts[label] = (statusCounts[label] || 0) + 1;
              });
              const summary = Object.entries(statusCounts).map(([status, count]) => `${count} ${status}`).join(", ");
              const allCompleted = Object.keys(statusCounts).every(s => s.toLowerCase() === "completed");
              return (
                <div style={{
                  padding: "10px 14px", marginBottom: "16px", borderRadius: "6px",
                  backgroundColor: allCompleted ? "#d4edda" : "#fff3cd",
                  border: `1px solid ${allCompleted ? "#c3e6cb" : "#ffc107"}`,
                  color: allCompleted ? "#155724" : "#856404",
                  fontWeight: 500, fontSize: "14px"
                }}>
                  {data.length} Deliveries — {summary}
                </div>
              );
            })()}
          {data.map((record, recordIdx) => {
            // Determine status color for the delivery header badge
            let headerStatusColor = "#6c757d";
            const headerStatus = (record.tracking_status || record.status || "").toLowerCase();
            switch (headerStatus) {
              case "completed": headerStatusColor = "#28a745"; break;
              case "completed_partial": case "partially completed": headerStatusColor = "#fd7e14"; break;
              case "failed": headerStatusColor = "#dc3545"; break;
              case "in_transit": case "in transit": headerStatusColor = "#007bff"; break;
              case "info_recv": case "info received": headerStatusColor = "#6f42c1"; break;
              case "dispatched": case "out_for_delivery": case "out for delivery": headerStatusColor = "#17a2b8"; break;
              case "scheduled": headerStatusColor = "#20c997"; break;
              case "on_hold": case "on hold": headerStatusColor = "#ffc107"; break;
              case "return": headerStatusColor = "#e83e8c"; break;
              default: headerStatusColor = "#6c757d";
            }

            return (
              <div key={recordIdx}>
                {/* Divider between delivery records (not before the first one) */}
                {recordIdx > 0 && <Divider sx={{ my: 3 }} />}

                {/* Delivery Record Header — DO Number, Date, Driver, Status */}
                <div style={{
                  display: "flex", justifyContent: "space-between", alignItems: "center",
                  padding: "10px 14px", marginBottom: "16px",
                  backgroundColor: "#f5f7fa", borderRadius: "6px", border: "1px solid #e0e0e0"
                }}>
                  <div style={{ display: "flex", gap: "16px", alignItems: "center", flexWrap: "wrap" }}>
                    <span style={{ fontWeight: 600, fontSize: "14px" }}>DO: {record.do_number || "-"}</span>
                    <span style={{ color: "#666", fontSize: "13px" }}>Date: {record.date || "-"}</span>
                    <span style={{ color: "#666", fontSize: "13px" }}>Driver: {record.assign_to || "-"}</span>
                  </div>
                  <Badge bg="" style={{ backgroundColor: headerStatusColor, textTransform: "capitalize" }} text="light">
                    {(record.tracking_status || record.status || "-").replace(/_/g, " ")}
                  </Badge>
                </div>

                {/* Milestones Table */}
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell><strong>Status / Reason</strong></TableCell>
                        <TableCell><strong>Date / Time</strong></TableCell>
                        <TableCell><strong>Driver</strong></TableCell>
                        <TableCell><strong>User</strong></TableCell>
                        <TableCell><strong>Proof</strong></TableCell>
                        <TableCell><strong>Map</strong></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {record.milestones && record.milestones.length > 0 ? (
                        record.milestones.map((ms, idx) => {
                          const statusLabel = (ms.status || "-").replace(/_/g, " ");
                          let statusColor = "#6c757d";
                          switch ((ms.status || "").toLowerCase()) {
                            case "completed": statusColor = "#28a745"; break;
                            case "completed_partial": statusColor = "#fd7e14"; break;
                            case "failed": statusColor = "#dc3545"; break;
                            case "in_transit": statusColor = "#007bff"; break;
                            case "info_recv": statusColor = "#6f42c1"; break;
                            case "dispatched": case "out_for_delivery": statusColor = "#17a2b8"; break;
                            case "scheduled": statusColor = "#20c997"; break;
                            case "on_hold": statusColor = "#ffc107"; break;
                            case "return": statusColor = "#e83e8c"; break;
                            case "head_to_delivery": statusColor = "#007bff"; break;
                            default: statusColor = "#6c757d";
                          }
                          return (
                            <TableRow key={idx}>
                              <TableCell>
                                <Badge bg="" style={{ backgroundColor: statusColor, textTransform: "capitalize" }} text="light">
                                  {statusLabel}
                                </Badge>
                              </TableCell>
                              <TableCell>
                                {ms.pod_at ? moment(ms.pod_at).format("YYYY-MM-DD @ HH:mm:ss") : "-"}
                              </TableCell>
                              <TableCell>{ms.assign_to || "-"}</TableCell>
                              <TableCell>{ms.user_name || "-"}</TableCell>
                              <TableCell>
                                {ms.pod_at ? moment(ms.pod_at).format("YYYY-MM-DD @ HH:mm:ss") : "-"}
                              </TableCell>
                              <TableCell>
                                {ms.pod_lat && ms.pod_lng ? (
                                  <a
                                    href={`https://www.google.com/maps?q=${ms.pod_lat},${ms.pod_lng}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                  >
                                    <MdLocationOn style={{ fontSize: "24px", color: "#007bff" }} />
                                  </a>
                                ) : "-"}
                              </TableCell>
                            </TableRow>
                          );
                        })
                      ) : (
                        <TableRow>
                          <TableCell colSpan={6} align="center" sx={{ py: 4, color: "#999" }}>
                            No tracking data available.
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>

                <Divider sx={{ my: 3 }} />

                {/* POD Details Section — per delivery record
                    Download buttons use the specific record's do_number and date */}
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "16px" }}>
                  <h5 style={{ margin: 0, fontWeight: 600 }}>POD Details</h5>
                  <div style={{ display: "flex", gap: "8px" }}>
                    <Button
                      variant="contained"
                      size="small"
                      disabled={!record || exportLoading === `label-${recordIdx}`}
                      sx={{ backgroundColor: "#4A90D9", textTransform: "none" }}
                      onClick={async () => {
                        if (!record.do_number) return;
                        setExportLoading(`label-${recordIdx}`);
                        try {
                          const res = await axios.get(
                            API_BASE_URL + `api/detrack/export/shipping_label/${encodeURIComponent(record.do_number)}?date=${encodeURIComponent(record.date)}&account=${encodeURIComponent(record._account || "encore")}`,
                            { headers: { "x-access-token": localStorage.getItem("token") }, responseType: "blob" }
                          );
                          const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
                          const link = document.createElement("a");
                          link.href = url;
                          link.download = `${record.do_number}-label.pdf`;
                          link.click();
                          window.URL.revokeObjectURL(url);
                        } catch (err) {
                          console.error("Shipping label download failed:", err);
                        } finally {
                          setExportLoading("");
                        }
                      }}
                    >
                      {exportLoading === `label-${recordIdx}` ? <CircularProgress size={16} sx={{ color: "#fff", marginRight: "4px" }} /> : <MdLocalShipping style={{ marginRight: 4 }} />} Shipping Label
                    </Button>
                    <Button
                      variant="outlined"
                      size="small"
                      disabled={!record || exportLoading === `pod-${recordIdx}`}
                      sx={{ textTransform: "none" }}
                      onClick={async () => {
                        if (!record.do_number) return;
                        setExportLoading(`pod-${recordIdx}`);
                        try {
                          const res = await axios.get(
                            API_BASE_URL + `api/detrack/export/pod/${encodeURIComponent(record.do_number)}?date=${encodeURIComponent(record.date)}&account=${encodeURIComponent(record._account || "encore")}`,
                            { headers: { "x-access-token": localStorage.getItem("token") }, responseType: "blob" }
                          );
                          const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
                          const link = document.createElement("a");
                          link.href = url;
                          link.download = `pod-${record.do_number}.pdf`;
                          link.click();
                          window.URL.revokeObjectURL(url);
                        } catch (err) {
                          console.error("POD download failed:", err);
                        } finally {
                          setExportLoading("");
                        }
                      }}
                    >
                      {exportLoading === `pod-${recordIdx}` ? <CircularProgress size={16} sx={{ marginRight: "4px" }} /> : <MdFileDownload style={{ marginRight: 4 }} />} POD
                    </Button>
                  </div>
                </div>

                <div style={{ display: "grid", gridTemplateColumns: "150px 1fr", rowGap: "16px", alignItems: "center" }}>
                  <label style={{ fontWeight: 500, color: "#555" }}>Tracking Link</label>
                  <div>
                    {record.tracking_link ? (
                      <a href={record.tracking_link} target="_blank" rel="noopener noreferrer" style={{ color: "#007bff" }}>
                        {record.tracking_link}
                      </a>
                    ) : <span style={{ color: "#999" }}>-</span>}
                  </div>

                  <label style={{ fontWeight: 500, color: "#555" }}>Tracking Status</label>
                  <div>
                    <Badge bg="" style={{ backgroundColor: headerStatusColor }} text="light">
                      {record.tracking_status || record.status || "-"}
                    </Badge>
                  </div>

                  {/* POD Images — per delivery record */}
                  <label style={{ fontWeight: 500, color: "#555" }}></label>
                  <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
                    {(() => {
                      const photos = [];
                      if (record) {
                        for (let i = 1; i <= 10; i++) {
                          const url = record[`photo_${i}_file_url`];
                          if (url) photos.push(url);
                        }
                      }
                      return photos.length > 0
                        ? photos.map((url, i) => (
                            <img
                              key={i}
                              src={url}
                              alt={`pod ${i + 1}`}
                              style={{
                                width: 80,
                                height: 80,
                                objectFit: "cover",
                                borderRadius: 4,
                                border: "1px solid #ddd",
                                cursor: "pointer",
                                transition: "transform 0.3s ease, zIndex 0s",
                                position: "relative",
                              }}
                              onMouseEnter={(e) => {
                                e.target.style.transform = "scale(5)";
                                e.target.style.zIndex = "999";
                                e.target.style.boxShadow = "0 4px 20px rgba(0,0,0,0.3)";
                              }}
                              onMouseLeave={(e) => {
                                e.target.style.transform = "scale(1)";
                                e.target.style.zIndex = "0";
                                e.target.style.boxShadow = "none";
                              }}
                            />
                          ))
                        : null;
                    })()}
                  </div>

                  <label style={{ fontWeight: 500, color: "#555" }}>Job Status</label>
                  <TextField
                    size="small"
                    value={(() => {
                      const statusMap = {
                        info_recv: "Info Received",
                        dispatched: "In Progress",
                        completed: "Delivered",
                        completed_partial: "Partially Delivered",
                        failed: "Not Delivered",
                        on_hold: "On Hold",
                        return: "Return",
                      };
                      const val = record.primary_job_status || record.status || "";
                      return statusMap[val] || val || "";
                    })()}
                    disabled
                    sx={{ minWidth: 250, "& .MuiInputBase-input.Mui-disabled": { WebkitTextFillColor: "#000", fontWeight: 500 } }}
                  />

                  <label style={{ fontWeight: 500, color: "#555" }}>POD Time</label>
                  <TextField size="small" value={record.pod_time || ""} disabled sx={{ maxWidth: 250, "& .MuiInputBase-input.Mui-disabled": { WebkitTextFillColor: "#000" } }} />

                  <label style={{ fontWeight: 500, color: "#555" }}>Status Note</label>
                  <TextField size="small" value={record.reason || ""} disabled sx={{ minWidth: 250, "& .MuiInputBase-input.Mui-disabled": { WebkitTextFillColor: "#000" } }} />

                  <label style={{ fontWeight: 500, color: "#555" }}>Received By</label>
                  <TextField size="small" value={record.received_by_sent_by || ""} disabled sx={{ maxWidth: 250, "& .MuiInputBase-input.Mui-disabled": { WebkitTextFillColor: "#000" } }} />

                  <label style={{ fontWeight: 500, color: "#555" }}>Note</label>
                  <TextField size="small" value={record.note || ""} disabled multiline rows={3} sx={{ maxWidth: 400, "& .MuiInputBase-input.Mui-disabled": { WebkitTextFillColor: "#000" } }} />

                  <label style={{ fontWeight: 500, color: "#555" }}>Texted At</label>
                  <TextField size="small" value={record.texted_at ? moment(record.texted_at).format("YYYY-MM-DD HH:mm:ss") : ""} disabled sx={{ maxWidth: 250, "& .MuiInputBase-input.Mui-disabled": { WebkitTextFillColor: "#000" } }} />

                  <label style={{ fontWeight: 500, color: "#555" }}>Called At</label>
                  <TextField size="small" value={record.called_at ? moment(record.called_at).format("YYYY-MM-DD HH:mm:ss") : ""} disabled sx={{ maxWidth: 250, "& .MuiInputBase-input.Mui-disabled": { WebkitTextFillColor: "#000" } }} />

                  <label style={{ fontWeight: 500, color: "#555" }}>Arrived At</label>
                  <TextField size="small" value={record.arrived_at ? moment(record.arrived_at).format("YYYY-MM-DD HH:mm:ss") : ""} disabled sx={{ maxWidth: 250, "& .MuiInputBase-input.Mui-disabled": { WebkitTextFillColor: "#000" } }} />

                  <label style={{ fontWeight: 500, color: "#555" }}>Mass POD Submission</label>
                  <div>
                    <Switch checked={record.mass_pod || false} disabled size="small" />
                  </div>

                </div>
              </div>
            );
          })}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default DetrackStatusDialog;
